import React, { useState, useMemo, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  Banknote, Plus, Copy, X, CheckCircle, AlertCircle,
  Loader2, Search, Download, ExternalLink, Clock,
  TrendingUp, AlertTriangle, RefreshCw, Receipt,
  FlaskConical, Wifi, WifiOff, CreditCard, Smartphone
} from "lucide-react";

const STATUS_CFG = {
  pending:   { label: "Pending",   cls: "bg-amber-100 text-amber-700" },
  paid:      { label: "Paid",      cls: "bg-emerald-100 text-emerald-700" },
  failed:    { label: "Failed",    cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500" },
  refunded:  { label: "Refunded",  cls: "bg-violet-100 text-violet-700" },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${c.cls}`}>
      {c.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const EMPTY = {
  customerName: "", customerEmail: "", serviceDescription: "",
  amount: "", includeGst: false, dueDate: "", notes: "",
};

export default function PaymentsInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [formError, setFormError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/payments/invoices");
      setInvoices(res.data.invoices || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for Firebase auth to resolve before fetching — prevents 401 on mount
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) fetchInvoices();
    });
    return unsub;
  }, [fetchInvoices]);

  const stats = useMemo(() => {
    const paid = invoices.filter(i => i.status === "paid");
    const pending = invoices.filter(i => i.status === "pending");
    return {
      total: invoices.length,
      paidCount: paid.length,
      pendingCount: pending.length,
      revenue: paid.reduce((s, i) => s + (i.totalAmount || 0), 0),
      outstanding: pending.reduce((s, i) => s + (i.totalAmount || 0), 0),
    };
  }, [invoices]);

  const filtered = useMemo(() => {
    let list = [...invoices];
    if (statusFilter !== "all") list = list.filter(i => i.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(i =>
        (i.customerName || "").toLowerCase().includes(s) ||
        (i.customerEmail || "").toLowerCase().includes(s) ||
        (i.invoiceNumber || "").toLowerCase().includes(s) ||
        (i.serviceDescription || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [invoices, statusFilter, search]);

  const gstAmt = form.amount ? Math.round(parseFloat(form.amount || 0) * 10) / 100 : 0;
  const totalAmt = form.amount
    ? parseFloat(form.amount || 0) + (form.includeGst ? gstAmt : 0)
    : 0;

  const handleCreate = async () => {
    setFormError("");
    if (!form.customerName || !form.customerEmail || !form.serviceDescription || !form.amount) {
      setFormError("Please fill in all required fields.");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/api/payments/invoices", form);
      setCreateResult({
        invoiceNumber: res.data.invoiceNumber,
        paymentLink: res.data.paymentLink,
        emailSent: res.data.emailSent,
        emailError: res.data.emailError,
      });
      setForm(EMPTY);
      fetchInvoices();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || "Failed to create invoice.");
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this invoice? This cannot be undone.")) return;
    setCancellingId(id);
    try {
      await api.delete(`/api/payments/invoices/${id}`);
      setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: "cancelled" } : i));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel.");
    } finally {
      setCancellingId(null);
    }
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const exportCsv = () => {
    const rows = [
      ["Invoice #", "Customer", "Email", "Service", "Amount", "GST", "Total", "Status", "Due Date", "Created", "Square Order ID"],
      ...filtered.map(i => [
        i.invoiceNumber, i.customerName, i.customerEmail, i.serviceDescription,
        i.amount, i.gst, i.totalAmount, i.status, i.dueDate || "", i.createdAt || "", i.squareOrderId || ""
      ])
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tinyglobal-invoices.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openModal = () => { setShowCreate(true); setCreateResult(null); setFormError(""); setForm(EMPTY); };
  const closeModal = () => { setShowCreate(false); setCreateResult(null); };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.get("/api/payments/test-connection");
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTesting(false);
    }
  };

  const openTest = () => { setShowTest(true); setTestResult(null); runTest(); };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 flex items-center gap-3">
            <Banknote size={28} /> Payments & Invoices
          </h1>
          <p className="text-slate-500 mt-1">Financial overview across the platform · Powered by Square</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openTest}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors shadow-sm"
          >
            <FlaskConical size={15} /> Test Connection
          </button>
          <button
            onClick={openModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl font-bold text-sm hover:bg-navy-800 transition-colors shadow-lg"
          >
            <Plus size={16} /> New Invoice
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: "Total Invoices", value: stats.total, icon: <Receipt size={18} />, color: "text-slate-600" },
          { label: "Paid", value: stats.paidCount, icon: <CheckCircle size={18} />, color: "text-emerald-600" },
          { label: "Pending", value: stats.pendingCount, icon: <Clock size={18} />, color: "text-amber-600" },
          { label: "Revenue Collected", value: `$${stats.revenue.toFixed(2)}`, icon: <TrendingUp size={18} />, color: "text-violet-600" },
          { label: "Outstanding", value: `$${stats.outstanding.toFixed(2)}`, icon: <AlertTriangle size={18} />, color: "text-rose-600" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className={`flex items-center gap-1.5 mb-2 ${s.color}`}>
              {s.icon}
              <span className="text-[10px] font-bold uppercase tracking-wide">{s.label}</span>
            </div>
            <p className="text-2xl font-black text-slate-800">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer, email, invoice number…"
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button onClick={fetchInvoices} title="Refresh" className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 hover:bg-slate-50 transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            <Download size={14} /> Export CSV
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader2 size={28} className="animate-spin text-slate-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-24 text-center">
            <Banknote size={36} className="text-slate-200 mb-3" />
            <p className="font-bold text-slate-400">No invoices found</p>
            <p className="text-sm text-slate-300 mt-1">Create your first invoice to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/60 border-b border-slate-100">
                <tr>
                  <th className="px-5 py-4">Invoice #</th>
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Amount</th>
                  <th className="px-5 py-4">GST</th>
                  <th className="px-5 py-4">Total (AUD)</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Due</th>
                  <th className="px-5 py-4">Created</th>
                  <th className="px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-4 text-xs font-mono font-bold text-navy-900 whitespace-nowrap">{inv.invoiceNumber}</td>
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-700 whitespace-nowrap">{inv.customerName}</p>
                      <p className="text-xs text-slate-400">{inv.customerEmail}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600 max-w-[180px] truncate">{inv.serviceDescription}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">${(inv.amount || 0).toFixed(2)}</td>
                    <td className="px-5 py-4 text-sm text-slate-500">{inv.gst > 0 ? `$${(inv.gst).toFixed(2)}` : "—"}</td>
                    <td className="px-5 py-4 text-sm font-bold text-slate-800">${(inv.totalAmount || 0).toFixed(2)}</td>
                    <td className="px-5 py-4"><StatusBadge status={inv.status} /></td>
                    <td className="px-5 py-4 text-xs text-slate-400 whitespace-nowrap">{inv.dueDate || "—"}</td>
                    <td className="px-5 py-4 text-xs text-slate-400 whitespace-nowrap">{timeAgo(inv.createdAt)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        {inv.paymentLink && (
                          <>
                            <button
                              onClick={() => copyLink(inv.paymentLink, inv.id)}
                              title="Copy payment link"
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-navy-900 transition-colors"
                            >
                              {copiedId === inv.id
                                ? <CheckCircle size={14} className="text-emerald-500" />
                                : <Copy size={14} />}
                            </button>
                            <a
                              href={inv.paymentLink}
                              target="_blank"
                              rel="noreferrer"
                              title="Open payment link"
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-navy-900 transition-colors"
                            >
                              <ExternalLink size={14} />
                            </a>
                          </>
                        )}
                        {inv.status === "pending" && (
                          <button
                            onClick={() => handleCancel(inv.id)}
                            disabled={cancellingId === inv.id}
                            title="Cancel invoice"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          >
                            {cancellingId === inv.id
                              ? <Loader2 size={14} className="animate-spin" />
                              : <X size={14} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Connection Modal */}
      {showTest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md">
            <div className="bg-[#071d2e] px-6 py-5 rounded-t-3xl flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <FlaskConical size={18} /> Square Connection Test
                </h2>
                <p className="text-slate-400 text-sm mt-0.5">Verifies credentials & available payment methods</p>
              </div>
              <button onClick={() => setShowTest(false)} className="text-slate-400 hover:text-white transition-colors p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {testing && (
                <div className="flex flex-col items-center py-8 gap-3">
                  <Loader2 size={28} className="animate-spin text-slate-400" />
                  <p className="text-sm text-slate-500 font-medium">Connecting to Square…</p>
                </div>
              )}

              {!testing && testResult && (
                <div className="space-y-5">
                  {/* Status */}
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${testResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                    {testResult.ok
                      ? <Wifi size={20} className="text-emerald-600 shrink-0" />
                      : <WifiOff size={20} className="text-red-500 shrink-0" />}
                    <div>
                      <p className={`font-bold text-sm ${testResult.ok ? "text-emerald-700" : "text-red-700"}`}>
                        {testResult.ok ? "Connected" : "Connection Failed"}
                      </p>
                      {testResult.error && <p className="text-xs text-red-600 mt-0.5">{testResult.error}</p>}
                    </div>
                    <span className={`ml-auto text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${testResult.env === "production" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {testResult.env || "sandbox"}
                    </span>
                  </div>

                  {testResult.ok && (
                    <>
                      {/* Location info */}
                      <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100 space-y-1.5">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Location</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Name</span>
                          <span className="font-semibold text-slate-800">{testResult.locationName || "—"}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">ID</span>
                          <span className="font-mono text-xs text-slate-600">{testResult.locationId}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Currency</span>
                          <span className="font-semibold text-slate-800">{testResult.currency}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Status</span>
                          <span className={`font-bold ${testResult.status === "ACTIVE" ? "text-emerald-600" : "text-red-500"}`}>{testResult.status}</span>
                        </div>
                      </div>

                      {/* Payment methods */}
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Payment Methods (Hosted Checkout)</p>
                        <div className="grid grid-cols-2 gap-2">
                          {(testResult.paymentMethods || []).map(m => (
                            <div
                              key={m.id}
                              className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-sm ${m.always ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 opacity-60"}`}
                            >
                              {m.id === "card" && <CreditCard size={16} className="text-emerald-600 mt-0.5 shrink-0" />}
                              {m.id === "apple_pay" && <Smartphone size={16} className="text-slate-700 mt-0.5 shrink-0" />}
                              {m.id === "google_pay" && <Smartphone size={16} className="text-blue-600 mt-0.5 shrink-0" />}
                              {m.id === "cash_app" && <Banknote size={16} className="text-slate-400 mt-0.5 shrink-0" />}
                              <div>
                                <p className={`font-semibold leading-tight ${m.always ? "text-slate-800" : "text-slate-500"}`}>{m.label}</p>
                                {m.note && <p className="text-[10px] text-slate-400 mt-0.5">{m.note}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2.5">Apple Pay &amp; Google Pay activate automatically on compatible browsers — no extra configuration needed.</p>
                      </div>
                    </>
                  )}

                  <button
                    onClick={runTest}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors"
                  >
                    <RefreshCw size={14} /> Re-test
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg my-auto">
            <div className="bg-[#071d2e] px-6 py-5 rounded-t-3xl flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">New Invoice</h2>
                <p className="text-slate-400 text-sm mt-0.5">Powered by Square · AUD</p>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-white transition-colors p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {createResult ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={30} className="text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-xl mb-1">Invoice Created!</h3>
                  <p className="text-slate-500 text-sm mb-3 font-mono">{createResult.invoiceNumber}</p>
                  {createResult.emailSent ? (
                    <p className="text-xs text-emerald-600 font-semibold mb-4">✓ Payment email sent to customer</p>
                  ) : (
                    <p className="text-xs text-amber-600 font-semibold mb-4">⚠ Email not sent{createResult.emailError ? `: ${createResult.emailError}` : " — check Render logs"}</p>
                  )}
                  <div className="bg-slate-50 rounded-2xl p-4 mb-5 text-left border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Payment Link</p>
                    <p className="text-xs text-slate-600 break-all font-mono leading-relaxed">{createResult.paymentLink}</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => copyLink(createResult.paymentLink, "result")}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-navy-900 text-white rounded-xl font-bold text-sm hover:bg-navy-800 transition-colors"
                    >
                      {copiedId === "result" ? <CheckCircle size={15} /> : <Copy size={15} />}
                      {copiedId === "result" ? "Copied!" : "Copy Link"}
                    </button>
                    <button onClick={closeModal} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200 transition-colors">
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: "Customer Name *", key: "customerName", span: 2, ph: "John Smith" },
                      { label: "Customer Email *", key: "customerEmail", span: 2, type: "email", ph: "client@company.com" },
                      { label: "Service Description *", key: "serviceDescription", span: 2, ph: "e.g. International Freight — Sydney to Melbourne" },
                    ].map(({ label, key, span, type = "text", ph }) => (
                      <div key={key} className={`col-span-${span}`}>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{label}</label>
                        <input
                          type={type}
                          value={form[key]}
                          onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={ph}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Amount (AUD) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={form.amount}
                          onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                          placeholder="100.00"
                          className="w-full pl-7 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Due Date</label>
                      <input
                        type="date"
                        value={form.dueDate}
                        onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.includeGst}
                          onChange={e => setForm(p => ({ ...p, includeGst: e.target.checked }))}
                          className="w-4 h-4 rounded accent-[#071d2e]"
                        />
                        <span className="text-sm font-semibold text-slate-700">Include GST (10%)</span>
                        {form.includeGst && form.amount && (
                          <span className="text-xs text-slate-400">+ ${gstAmt.toFixed(2)} GST</span>
                        )}
                      </label>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
                      <textarea
                        rows={2}
                        value={form.notes}
                        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                        placeholder="Optional notes for this invoice…"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 transition-all resize-none"
                      />
                    </div>
                  </div>

                  {form.amount && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-600">Total Charged</span>
                      <span className="text-xl font-black text-navy-900">AUD ${totalAmt.toFixed(2)}</span>
                    </div>
                  )}

                  {formError && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-xl text-sm font-medium border border-red-200">
                      <AlertCircle size={15} /> {formError}
                    </div>
                  )}

                  <button
                    onClick={handleCreate}
                    disabled={creating}
                    className="w-full py-3.5 bg-navy-900 text-white font-bold rounded-xl text-sm hover:bg-navy-800 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
                  >
                    {creating ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}
                    {creating ? "Creating…" : "Create Invoice & Generate Payment Link"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
